import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, labelize } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Play, PauseCircle, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";

export default function QualityAlerts() {
  const [meta, setMeta] = useState({ metrics: [], target_types: [], pause_targets: [] });
  const [alerts, setAlerts] = useState([]);
  const [status, setStatus] = useState([]);
  const [pauses, setPauses] = useState([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [showPauseForm, setShowPauseForm] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [alertForm, setAlertForm] = useState({
    name: "", metric: "bad_lead_rate", target_type: "affiliate", target_key: "",
    threshold_pct: 10, window_days: 7, min_sample: 25, action: "notify", notify_emails: "",
  });
  const [pauseForm, setPauseForm] = useState({ target_type: "affiliate", target_key: "", reason: "" });
  const [evaluating, setEvaluating] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    try {
      await loadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      setLoadError(e.response?.status === 403 ? "You don't have access to Quality Alerts. Ask an admin for the integrations permission."
        : (typeof d === "string" ? d : "Couldn't load Quality Alerts. Try again."));
    }
  };
  const loadAll = async () => {
    const [m, a, s, p, af, cl, cp] = await Promise.all([
      api.get("/quality/_meta"),
      api.get("/quality/alerts"),
      api.get("/quality/status"),
      api.get("/quality/pauses?active_only=true"),
      api.get("/organizations?type=affiliate"),
      api.get("/organizations?type=client"),
      api.get("/campaigns"),
    ]);
    setMeta(m.data); setAlerts(a.data.alerts); setStatus(s.data.status);
    setPauses(p.data.pauses); setAffiliates(af.data.organizations);
    setClients(cl.data.organizations); setCampaigns(cp.data.campaigns);
    setLoadError(null);
  };
  useEffect(() => { load(); }, []);

  const submitAlert = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...alertForm,
        threshold_pct: Number(alertForm.threshold_pct),
        window_days: Number(alertForm.window_days),
        min_sample: Number(alertForm.min_sample),
        notify_emails: alertForm.notify_emails ? alertForm.notify_emails.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/quality/alerts", payload);
      toast.success("Alert created");
      setShowAlertForm(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const { data } = await api.post("/quality/evaluate");
      toast.success(`Evaluated ${data.results.length} alerts`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setEvaluating(false); }
  };

  const submitPause = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quality/pauses", pauseForm);
      toast.success("Paused");
      setShowPauseForm(false);
      setPauseForm({ target_type: "affiliate", target_key: "", reason: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unpause = async (id) => {
    if (!confirm("Unpause this entity?")) return;
    try {
      await api.delete(`/quality/pauses/${id}`);
      toast.success("Unpaused");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteAlert = async (id) => {
    if (!confirm("Delete this alert?")) return;
    await api.delete(`/quality/alerts/${id}`);
    toast.success("Deleted");
    load();
  };

  const targetKeyOptions = () => {
    if (alertForm.target_type === "affiliate") return affiliates.map((a) => ({ v: a.id, l: a.name }));
    if (alertForm.target_type === "public_source") return affiliates.map((a) => ({ v: a.public_source_id, l: `${a.name} · ${a.public_source_id}` }));
    if (alertForm.target_type === "campaign") return campaigns.map((c) => ({ v: c.id, l: c.name }));
    if (alertForm.target_type === "client") return clients.map((c) => ({ v: c.id, l: c.name }));
    return null; // sub_id → free text
  };

  const pauseKeyOptions = () => {
    if (pauseForm.target_type === "affiliate") return affiliates.map((a) => ({ v: a.id, l: a.name }));
    if (pauseForm.target_type === "public_source") return affiliates.map((a) => ({ v: a.public_source_id, l: `${a.name} · ${a.public_source_id}` }));
    if (pauseForm.target_type === "campaign") return campaigns.map((c) => ({ v: c.id, l: c.name }));
    if (pauseForm.target_type === "client") return clients.map((c) => ({ v: c.id, l: c.name }));
    return null;
  };

  if (loadError) {
    return <div className="brut-card p-6 text-sm" data-testid="quality-load-error">{loadError}</div>;
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Guardrails</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Quality alerts</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={evaluate} disabled={evaluating} data-testid="quality-evaluate-btn"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100 disabled:opacity-60">
            <ArrowsClockwise size={12} className="inline mr-1" /> {evaluating ? "Evaluating…" : "Evaluate now"}
          </button>
          <button onClick={() => setShowAlertForm((v) => !v)} data-testid="new-alert-btn"
            className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
            <Plus size={12} className="inline mr-1" /> New alert
          </button>
        </div>
      </div>

      {showAlertForm && (
        <form onSubmit={submitAlert} className="brut-card p-5 mb-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="alert-form">
          <div className="md:col-span-3">
            <label className="label-mono">Name</label>
            <input data-testid="alert-name" className="w-full border border-black px-3 py-2 mt-1"
              value={alertForm.name} onChange={(e) => setAlertForm({ ...alertForm, name: e.target.value })}
              placeholder="e.g. Affiliate ABC bad-lead rate > 15%" />
          </div>
          <div>
            <label className="label-mono">Metric</label>
            <select data-testid="alert-metric" className="w-full border border-black px-2 py-1.5 mt-1"
              value={alertForm.metric} onChange={(e) => setAlertForm({ ...alertForm, metric: e.target.value })}>
              {meta.metrics.map((m) => <option key={m} value={m}>{labelize(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono">Target</label>
            <select data-testid="alert-target-type" className="w-full border border-black px-2 py-1.5 mt-1"
              value={alertForm.target_type}
              onChange={(e) => setAlertForm({ ...alertForm, target_type: e.target.value, target_key: "" })}>
              {meta.target_types.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono">Target value</label>
            {targetKeyOptions() ? (
              <select data-testid="alert-target-key" className="w-full border border-black px-2 py-1.5 mt-1"
                value={alertForm.target_key} onChange={(e) => setAlertForm({ ...alertForm, target_key: e.target.value })}>
                <option value="">— any —</option>
                {targetKeyOptions().map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <input data-testid="alert-target-key" className="w-full border border-black px-3 py-2 mt-1"
                value={alertForm.target_key} onChange={(e) => setAlertForm({ ...alertForm, target_key: e.target.value })}
                placeholder="e.g. google-cpc" />
            )}
          </div>
          <div>
            <label className="label-mono">Threshold %</label>
            <input data-testid="alert-threshold" type="number" step="0.01"
              className="w-full border border-black px-3 py-2 mt-1"
              value={alertForm.threshold_pct} onChange={(e) => setAlertForm({ ...alertForm, threshold_pct: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Window (days)</label>
            <input type="number" className="w-full border border-black px-3 py-2 mt-1"
              value={alertForm.window_days} onChange={(e) => setAlertForm({ ...alertForm, window_days: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Min sample size</label>
            <input type="number" className="w-full border border-black px-3 py-2 mt-1"
              value={alertForm.min_sample} onChange={(e) => setAlertForm({ ...alertForm, min_sample: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Action</label>
            <select data-testid="alert-action" className="w-full border border-black px-2 py-1.5 mt-1"
              value={alertForm.action} onChange={(e) => setAlertForm({ ...alertForm, action: e.target.value })}>
              <option value="notify">Notify only</option>
              <option value="auto_pause">Auto-pause target</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="label-mono">Notify emails (comma-separated)</label>
            <input className="w-full border border-black px-3 py-2 mt-1"
              value={alertForm.notify_emails} onChange={(e) => setAlertForm({ ...alertForm, notify_emails: e.target.value })}
              placeholder="ops@example.com, alerts@example.com" />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" data-testid="alert-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">Create</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="brut-card p-5 lg:col-span-2 overflow-x-auto">
          <div className="font-chivo font-bold uppercase tracking-tight mb-3">Configured alerts</div>
          <table className="w-full text-sm font-mono min-w-[720px]">
            <thead>
              <tr className="border-b border-black text-[10px] uppercase tracking-widest">
                <th className="py-2 text-left">Name</th>
                <th className="py-2 text-left">Metric</th>
                <th className="py-2 text-left">Scope</th>
                <th className="py-2 text-left">Threshold</th>
                <th className="py-2 text-left">Current</th>
                <th className="py-2 text-left">Firing?</th>
                <th className="py-2 text-left">Action</th>
                <th className="py-2 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const st = status.find((s) => s.alert_id === a.id);
                return (
                  <tr key={a.id} className="border-b border-zinc-200" data-testid={`alert-row-${a.id}`}>
                    <td className="py-2">{a.name}</td>
                    <td className="py-2">{labelize(a.metric)}</td>
                    <td className="py-2">{a.target_type}{a.target_key ? ` · ${a.target_key.slice(0, 18)}` : ""}</td>
                    <td className="py-2">{a.threshold_pct}%</td>
                    <td className="py-2">{st?.value_pct != null ? `${st.value_pct}%` : "—"}</td>
                    <td className="py-2">{st?.firing ? <span className="bg-red-600 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 border border-black">FIRING</span> : <span className="text-zinc-500">quiet</span>}</td>
                    <td className="py-2">{a.action}</td>
                    <td className="py-2">
                      <button onClick={() => deleteAlert(a.id)} data-testid={`alert-delete-${a.id}`}
                        className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                        <Trash size={10} className="inline" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!alerts.length && <tr><td colSpan={8} className="py-6 text-center label-mono">No alerts configured</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="brut-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-chivo font-bold uppercase tracking-tight">Active pauses</div>
            <button onClick={() => setShowPauseForm((v) => !v)} data-testid="new-pause-btn"
              className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
              <PauseCircle size={10} className="inline" /> New
            </button>
          </div>
          {showPauseForm && (
            <form onSubmit={submitPause} className="border border-black p-3 mb-3 space-y-2" data-testid="pause-form">
              <div>
                <label className="label-mono">Target type</label>
                <select data-testid="pause-target-type" className="w-full border border-black px-2 py-1.5 mt-1"
                  value={pauseForm.target_type} onChange={(e) => setPauseForm({ ...pauseForm, target_type: e.target.value, target_key: "" })}>
                  {meta.pause_targets.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono">Target key</label>
                {pauseKeyOptions() ? (
                  <select data-testid="pause-target-key" className="w-full border border-black px-2 py-1.5 mt-1"
                    value={pauseForm.target_key} onChange={(e) => setPauseForm({ ...pauseForm, target_key: e.target.value })}>
                    <option value="">— select —</option>
                    {pauseKeyOptions().map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input data-testid="pause-target-key" className="w-full border border-black px-3 py-2 mt-1"
                    value={pauseForm.target_key} onChange={(e) => setPauseForm({ ...pauseForm, target_key: e.target.value })}
                    placeholder="Value" />
                )}
              </div>
              <div>
                <label className="label-mono">Reason</label>
                <input className="w-full border border-black px-3 py-2 mt-1"
                  value={pauseForm.reason} onChange={(e) => setPauseForm({ ...pauseForm, reason: e.target.value })} />
              </div>
              <button type="submit" data-testid="pause-submit"
                className="w-full bg-black text-white text-xs uppercase tracking-widest py-1.5">Pause</button>
            </form>
          )}
          <ul className="text-sm font-mono space-y-2">
            {pauses.map((p) => (
              <li key={p.id} className="border-b border-zinc-200 pb-2" data-testid={`pause-row-${p.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.target_type}</div>
                <div>{p.target_key}</div>
                <div className="text-[11px] text-zinc-600">{p.reason || "—"}</div>
                <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                  <span>{fmtDate(p.created_at)}</span>
                  <button onClick={() => unpause(p.id)} data-testid={`unpause-${p.id}`}
                    className="border border-black px-2 py-0.5 uppercase tracking-widest hover:bg-black hover:text-white">
                    <Play size={9} className="inline" /> Unpause
                  </button>
                </div>
              </li>
            ))}
            {!pauses.length && <li className="label-mono">No active pauses</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
